import React, { useMemo } from "react";
import type { Table } from "@tanstack/react-table";
import TheadCell from "./TheadCell";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";

interface TheadProps {
  table: Table<unknown>;
  size?: "small" | "large";
  "data-testid"?: string;
  enableTableViewEdit?: boolean;
  lastPinnedColumn?: number;
  rowHeight?: number;
  getSelectAllAlertComponent?:
    | ((table: Table<unknown>) => React.ReactNode)
    | null;
  hasComplexSorting?: boolean;
  sortOnHeaderClick?: boolean;
}

const Thead: React.FC<TheadProps> = ({
  table,
  size,
  "data-testid": dataTestId,
  enableTableViewEdit,
  lastPinnedColumn,
  rowHeight,
  getSelectAllAlertComponent,
  hasComplexSorting,
  sortOnHeaderClick,
}) => {
  const headerGroups = useMemo(
    () => table.getHeaderGroups(),
    [
      table,
      table?.getVisibleFlatColumns(),
      table.getState()?.columnOrder,
      table.getState()?.columnPinning,
      table?.getGroupedRowModel(),
    ]
  );

  const hasFilteredColumn = useMemo(
    () =>
      !!headerGroups?.find((headerGroup) =>
        headerGroup?.headers?.find((header) => header.column.getCanFilter())
      ),

    [headerGroups]
  );

  return (
    <thead
      className={`thead ${size === "small" ? "is-small" : "is-large"}`}
      data-testid={dataTestId}
    >
      {headerGroups?.map((headerGroup) => {
        return (
          <tr
            className={hasFilteredColumn ? " tr is-filtered" : "tr"}
            key={headerGroup.id}
          >
            <SortableContext
              items={
                enableTableViewEdit ? table?.getAllLeafColumns() || [] : []
              }
              strategy={horizontalListSortingStrategy}
            >
              {headerGroup.headers.map((header) => {
                return (
                  <TheadCell
                    rowHeight={rowHeight}
                    dataTestId={dataTestId}
                    enableTableViewEdit={enableTableViewEdit}
                    header={header}
                    numOfRows={
                      table.getIsAllRowsSelected() &&
                      !!getSelectAllAlertComponent
                        ? table?.getRowModel()?.rows?.length + 1
                        : table?.getRowModel()?.rows?.length
                    }
                    lastPinnedColumn={lastPinnedColumn}
                    hasComplexSorting={hasComplexSorting}
                    sortOnHeaderClick={sortOnHeaderClick}
                  />
                );
              })}
            </SortableContext>
          </tr>
        );
      })}
      {table.getIsAllRowsSelected() && !!getSelectAllAlertComponent && (
        <tr className={"select-all-banner"}>
          <div className={"select-all-banner-inner"} />
          {getSelectAllAlertComponent(table)}
        </tr>
      )}
    </thead>
  );
};

export default Thead;
